import {useFormbird} from '../formbird/FormbirdContext';
import React, {useEffect, useState, useMemo} from 'react';
import {
    DataGridPremium,
    GridColDef,
    GridValueGetterParams,
    GridRowModel,
    GridToolbarContainerProps,
    GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarDensitySelector,
    GridRowId, useGridApiRef,
    GridLogicOperator,
    GridRenderCellParams,
    GridValueFormatterParams
} from '@mui/x-data-grid-premium';
import {Box, LinearProgress, Typography} from '@mui/material';
import {convertToCustomElement} from '../formbird/CustomElementWrapper';
import { format, isWithinInterval } from 'date-fns';
import { useWatchedFields, useDefinition } from './utils/Hooks';
import { UrlService } from '../services';
interface TableColumn {
    displayName: string;
    field: string;
    urlOpenIn: string;
    width: string;
    href?: string;
    type?: string;
}

interface KpiCaseActionSummaryComponentDefinition {
    dataField: string;
    datesField: string;
    kpisField: string;
    childDataField: string;
    dateFilterField: string;
    parentGridColumns: TableColumn[];
    childGridColumns: TableColumn[];
    expandedRows: boolean;
    filter: string;
    dataFilter: string; 
}

function filterData(data: any, filterTermsStr: string) {
    // Define helper functions for checking values based on operator
    function checkContains(fieldValue:any, value: any) {
      if (typeof fieldValue !== 'string') {
        throw new Error(`Field value for 'contains' must be a string: ${fieldValue}`);
      }
      return fieldValue && fieldValue.toLowerCase().includes(value.toLowerCase());
    }
    function checkEquals(fieldValue:any, value: any) {
      return fieldValue === value;
    }
    function checkIsAnyOf(fieldValue:any, valueArray:any) {
      if (fieldValue === undefined) { fieldValue = null; }
      return valueArray.includes(fieldValue);
    }
    function checkIsVoid(fieldValue:any) {
      return !fieldValue;
    }
    function checkIsEmpty(fieldValue:any) {
      return fieldValue?.length === 0;
    }
    function checkExists(fieldValue:any) {
      return !!fieldValue;
    }
  
    function checkField(fieldValue:any, operator, value:any) { 
      switch (operator) {
      case 'contains':
          return checkContains(fieldValue, value);
      case 'equals':
          return checkEquals(fieldValue, value);
      // Parse value back to array
      case 'isAnyOf':
          return checkIsAnyOf(fieldValue, value);
      case 'isEmpty': 
          return checkIsEmpty(fieldValue);
      case 'exists':
          return checkExists(fieldValue);
      case 'isVoid': 
          return checkIsVoid(fieldValue);
      default:
          throw new Error(`Unsupported operatorValue: ${operator}`);
      }
    }
  
    function checkValidField(field:any){ 
      if (!(field.length>0) || (typeof field !== 'string')){ throw new Error(`field defined incorrectly: ${field}`); }
      const fieldPath = field.split('.'); // Split field into array if nested
      if (!(fieldPath?.length>0) || fieldPath?.length>2){ throw new Error(`field defined incorrectly: ${field}`); }
      return fieldPath;
    }
  
    function checkValidOperator(operator) { 
      const validOperators = ['contains', 'equals', 'isAnyOf', 'isEmpty', 'exists', 'isVoid'];
      if (typeof operator !== 'string'){ throw new Error(`operatorValue invalid: ${operator}`); }
      if (!validOperators.includes(operator)){ throw new Error(`operatorValue not a valid operator: ${operator}`); }
    }

    if (!filterTermsStr || (filterTermsStr == '') || (!(filterTermsStr?.length>0)) ){ 
        return data;
    }
    let filterTerms = getFilter(filterTermsStr, false);

    if (!filterTerms || (!(filterTerms?.length>0)) ){ 
        return data;
    }
    const dataTemp = JSON.parse(JSON.stringify(data));
    // Loop through each data object
    return dataTemp.filter(item => {
      // Loop through each filter term
      return filterTerms.every((term: any) => { //
        const { field, value } = term;
        const operator = term.operatorValue ?? term.operator;
        // error catching
        const fieldPath = checkValidField(field);
        checkValidOperator(operator);
        let fieldValue = item[fieldPath[0]];
        
        // get nested field if needed
        if (typeof fieldValue === 'object' && fieldValue?.length>0 && fieldPath.length>1){
          const newFieldValue = fieldValue.filter( subItem => {
              const subFieldValue = subItem[fieldPath[1]];
              return checkField(subFieldValue, operator, value);
          });
          // return if atleast 1 item meets the filter
          if (newFieldValue.length>0) { return true; }
          else { return false; }
        }
        else { // check field
          // Check value based on operator
          return checkField(fieldValue, operator, value);
        }
      });
    });
}

const tableToGridColumns = (columns: TableColumn[]): GridColDef[] => { 
  console.debug('tableToGridColumns input', columns);

  const c = columns.map((col) => {
    let type;
    let valueFormatter: ((params: GridValueFormatterParams<unknown>) => string) | undefined;
    let getter: ((params: GridValueGetterParams) => unknown) | undefined;
    if (col.type === 'numeric') {
        type = 'number';
    } else if (col.type === 'date') {
        type = 'dateTime';
        getter = (params: GridValueGetterParams) => {
            const value = params.value;
            if (!value) {
                return null
            }
            let date: any;
            date = new Date(params.value);
            if (isNaN(date)) {
                console.debug('invalid date', params.row);
                return 'Invalid Date'
            }
            return date
        };
        valueFormatter = (params) => {
            const date = params.value;
            if (typeof date === 'string') {
                return date
            } else if (date === null) {
                return ''
            } else if (date instanceof Date) {
                return format(date, 'dd/MM/yyyy p')
            } else {
                console.error('Error while formatting date', date)
                return 'Error while parsing date'
            }
        };
    }
    const renderCell = col.href === undefined ? undefined : (params: GridRenderCellParams) => {
        let href = col.href ?? '';
        const matches = [...href.matchAll(/{{{([a-zA-Z]+)}}}/g)];

        matches.forEach(([org, key]) => {
            href = href.replace(org, params.row[key])
        })
        const onLinkClick = (e: React.MouseEvent<unknown>) => {
            UrlService.openLink(href, col.urlOpenIn);
        }
        const divStyle = {
            color:'#1a76d2',
            bold:true,
            'text-decoration':'underline'
        }
        return (
            <div 
                style={divStyle}
                onClick={onLinkClick}>
                {params.value}
            </div>
        );
    };
    return {
        headerName: col.displayName,
        field: col.field,
        flex: parseInt(col.width, undefined),
        type,
        valueGetter: getter,
        valueFormatter,
        renderCell,
    };
  });
  console.debug('tableToGridColumns', c)
  return c;
}

const KpiCaseActionDetailPanel = (props: { rows: GridRowModel[], columns: TableColumn[] }) => {
    console.log('kpiActionDetail props', props);
    const columns: GridColDef[] = useMemo(() => tableToGridColumns(props.columns), [props.columns]);
    return (
        <DataGridPremium
            autoHeight
            disableRowSelectionOnClick
            columns={columns}
            rows={props.rows}
            getRowId={(r) => r.documentId}
            hideFooter
        />
    );
};

const useRows = (compDef: any) => {
    const { caseData: docCaseData, reportDates }  = useWatchedFields<{ caseData: any, reportDates: string | undefined }>({
        'caseData': 'caseData',
        'reportDates': 'reportDates',
    });
    // console.debug('[case action] rows. Length:', docCaseData?.length, 'reportDates filter:', !!reportDates, 'dataFilter:', (compDef?.dataFilter?.length>0), compDef);
    const caseDataLocal = docCaseData ?? [];

    if (reportDates === undefined && !(compDef?.dataFilter?.length>0)) {
        return caseDataLocal
    }

    let data 
    if (reportDates === undefined) {data = caseDataLocal;}
    else { 
        const temp = JSON.parse(JSON.stringify(caseDataLocal));
        data = temp.filter((it: any) => {
                                        const [startStr, endStr] = reportDates;
                                        const start = new Date(startStr);
                                        const end = new Date(endStr);
                                        const filterDate = new Date(it[compDef.dateFilterField]);
                                        return isWithinInterval(filterDate, { start, end });
                                    });
    }
    if (compDef?.dataFilter?.length>0) {
        console.debug('kpiCaseActionSummary filtering data', compDef.dataFilter, data);
        return filterData(data, compDef.dataFilter);
    }
    else { 
        return data;
    }
}

const CustomToolbar = (props: GridToolbarContainerProps) => (
    <GridToolbarContainer {...props}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
    </GridToolbarContainer>
);

const getFilter = (filterStr: string, idsOn = true) => {
    let filterItems = [];

    try { // eg, parsed version: [{ field: 'rating', operator: '>', value: '2.5' }]
        if (filterStr?.length){
            let temp = filterStr;
            temp = temp.replaceAll(`'`, '"');
            filterItems = JSON.parse(temp);
        }
        if (idsOn){
            filterItems.forEach( (item: any, i: any) => { item.id = i+1; });
        }
    }
    catch (err){ // if doesnt exist or fails to parse
        console.error('Cannot parse kpiCaseActionSummary.filter. Please check syntax and try again.');
        return [];
    }
    return filterItems;
}

const KpiCaseActionSummary = () => {
    const apiRef = useGridApiRef();

    const fb = useFormbird<KpiCaseActionSummaryComponentDefinition>();
    const initialFilter = getFilter(fb.componentDefinition.filter? fb.componentDefinition.filter : '');
    const intialDataFilter = fb.componentDefinition.dataFilter? fb.componentDefinition.dataFilter : '';

    const columns: GridColDef[] = useMemo(
        () => tableToGridColumns(fb.componentDefinition.parentGridColumns),
        [fb.componentDefinition.parentGridColumns]);

    const rows =  useRows(fb.componentDefinition);  // want this to update as an override

    const getRowId = (r: any): any => r.businessRef;
    
    let rowIds: GridRowId[] = rows.map(getRowId);

    const [filterStr, setFilterStr] = useState(() => fb.componentDefinition.filter? fb.componentDefinition.filter : '');
    const [filter, setFilter] = useState(() => getFilter( fb.componentDefinition.filter? fb.componentDefinition.filter : '' ));
    const [dataFilter, setDataFilter] = useState(() => getFilter( fb.componentDefinition.dataFilter? fb.componentDefinition.dataFilter: '' ));
    
    let compDef = useDefinition( (newValue: any) => {
        // console.debug('kpiAction change detected', newValue);
        if (newValue?.filter !== filterStr ){
            setFilterStr(newValue.filter? newValue.filter : '');
            const tempFilter = getFilter(newValue.filter);
            setFilter(tempFilter);
            apiRef.current.setFilterModel( {items: tempFilter, logicOperator: GridLogicOperator.And} ); 
        }
    });

    useEffect(() => {
        if (fb.componentDefinition.expandedRows && apiRef?.current) {
            apiRef.current.setExpandedDetailPanels(rowIds);
        }
    }, [apiRef, fb.componentDefinition.expandedRows, ...rowIds]);

    return <>
        <Typography variant='body1'>{fb.componentDefinition.label}</Typography>
        <div style={{overflowX:'auto', width:'100%'}}>
            <Box sx={{
                height: '100%',
                display: 'flex'
            }}>
                <Box sx={{ flexGrow: 1 }}>
                    <DataGridPremium
                        autoHeight
                        disableRowSelectionOnClick
                        rows={rows}
                        columns={columns}
                        apiRef={apiRef}
                        getRowId={getRowId}
                        components={{
                            Toolbar: CustomToolbar, // #19407
                            LoadingOverlay: LinearProgress,
                        }}
                        componentsProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: { debounceMs: 500 }
                            },
                            baseSelect: {
                                native: false,
                            }
                        }}
                        getDetailPanelContent={({ row }) => <KpiCaseActionDetailPanel
                            columns={fb.componentDefinition.childGridColumns}
                            rows={row[fb.componentDefinition.childDataField]}
                        />}
                        getDetailPanelHeight={() => 'auto'}
                        initialState={{
                            filter: {
                                filterModel: {
                                    items: initialFilter,
                                    logicOperator: GridLogicOperator.And,
                                },
                            },
                        }}
                        onFilterModelChange={ (model, details) => {
                            // console.debug('model changed', model);
                        }}
                    />
                </Box>
            </Box>
        </div>
    </>;
}

convertToCustomElement('gww-kpi-case-action-summary', KpiCaseActionSummary);
export default KpiCaseActionSummary;
